import axios from "axios";
import { BASE_URL } from "../../config";
import { getUser } from './auth';

const apiClient = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
})
apiClient.interceptors.request.use(config => {
    const user = getUser();

    // jeśli endpoint wymaga auth, a usera/brak tokena -> wyloguj i anuluj
    if (config.requiresAuth && (!user || !user.token)) {
        window.dispatchEvent(new CustomEvent('auth:logout', { detail: { reason: 'no-user' } }));
        // rzucamy kontrolowany błąd; komponenty nie muszą nic z tym robić
        return Promise.reject(new axios.Cancel('AUTH_REQUIRED'));
    }

    if (user?.token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${user.token}`;
    }
    return config;
});

// Obsługa wygasłego tokena
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {

        if (axios.isCancel?.(error) && error.message === 'AUTH_REQUIRED') {
            // już przekierowaliśmy - nic nie rób
            return Promise.reject(error);
        }

        const originalRequest = error.config;
        if (!originalRequest) return Promise.reject(error);

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const user = getUser();
            if (!user) {
                window.dispatchEvent(new CustomEvent('auth:logout'));
                return Promise.reject(error);
            }
            if (!user?.refreshToken) {
                return Promise.reject(error); // brak refresh tokena
            }
            if (isRefreshing) {
                // Jeśli już trwa odświeżanie, kolejkuj żądania
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token) => {
                            originalRequest.headers = originalRequest.headers || {};
                            originalRequest.headers['Authorization'] = 'Bearer ' + token;
                            resolve(apiClient(originalRequest));
                        },
                        reject: (err) => reject(err)
                    });
                });
            }
            isRefreshing = true;
            try {
                const response = await axios.post(`${BASE_URL}/auth/refresh`, {
                    token: user.refreshToken
                });
                const newToken = response.data?.data?.token ?? response.data?.token;
                const newRefreshToken = response.data?.data?.refreshToken ?? response.data?.refreshToken;

                // Zapisujemy nowy token
                const updatedUser = { ...user, token: newToken, refreshToken: newRefreshToken };
                localStorage.setItem('user', JSON.stringify(updatedUser));

                // Aktualizujemy Authorization
                apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;

                processQueue(null, newToken);

                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
                return apiClient(originalRequest);
            } catch (err) {
                processQueue(err, null);
                localStorage.removeItem('user');
                window.dispatchEvent(new CustomEvent('auth:logout'));
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);

export { apiClient };
